import React from "react";

/* ============================================================================
 * HomeMedia — ภาพจำลอง (mock) ของทรัพย์ วาดด้วย SVG ล้วน
 *
 * ใช้เป็นภาพสำรองเมื่อประกาศ/เนื้อหานั้นยังไม่ได้อัปโหลดรูปจริง — ถ้าปล่อยเป็น
 * กล่องสีเทาเปล่า ๆ หน้าเว็บจะดูเหมือนโหลดไม่ขึ้น ส่วนที่มีรูปจริงแล้วจะส่ง prop
 * `src` (URL จาก storage) เข้ามา คอมโพเนนต์จะ render <img> แทนทันที
 *
 * props
 *   kind  : tower | lowrise | house | town | shop | land | skyline | abstract
 *   tone  : 0-7 ชุดสีบรรยากาศ (เช้า/กลางวัน/เย็น) ทำให้การ์ดแต่ละใบไม่ซ้ำกัน
 *   ratio : สัดส่วนกล่องภาพ (height / width) ค่าเริ่มต้น 0.68
 *   src   : ถ้ามี = ใช้ภาพจริง
 * ========================================================================== */

/* ชุดสีบรรยากาศ 8 แบบ — คุมให้อยู่ในโทนธรรมชาติ ไม่ฉูดฉาด */
const TONES = [
  { sky: ["#cfe4f7", "#eef6fb"], sun: "#fde68a", far: "#b8ccdd", mid: "#dbe6ee", near: "#f2f6f8", win: "#7ea8c9", roof: "#4a5b6b", ground: "#dfe9dc", tree: "#7fa06b" },
  { sky: ["#fbe3c8", "#fdf4e6"], sun: "#fcd9a0", far: "#d8c2ab", mid: "#efe0cd", near: "#faf1e5", win: "#c79a63", roof: "#6b5340", ground: "#e6e2ce", tree: "#87a06a" },
  { sky: ["#d3e7e2", "#f0f7f4"], sun: "#c7ead8", far: "#b3ccc6", mid: "#d8e8e3", near: "#f1f8f5", win: "#6fa397", roof: "#3f5a54", ground: "#dde9d9", tree: "#6f9a72" },
  { sky: ["#dfe3f5", "#f5f6fc"], sun: "#e8e4fb", far: "#c2c7de", mid: "#dee1ef", near: "#f4f5fb", win: "#8a90bb", roof: "#4b4f6b", ground: "#e2e7de", tree: "#7d9c74" },
  { sky: ["#f7dfe0", "#fdf1f0"], sun: "#fbd0cd", far: "#dcc0c1", mid: "#eedcdb", near: "#fbf2f1", win: "#c08b8c", roof: "#6b4a4b", ground: "#e8e4d8", tree: "#83a06d" },
  { sky: ["#dcecd6", "#f3f9ef"], sun: "#e3f2c9", far: "#bdd0b4", mid: "#dbe8d4", near: "#f2f8ee", win: "#7fa26f", roof: "#47603f", ground: "#dfe9d6", tree: "#6b9560" },
  { sky: ["#cfe0ea", "#eef4f8"], sun: "#d8ebf5", far: "#adc2ce", mid: "#d3e0e7", near: "#eff5f8", win: "#6d92a6", roof: "#3d5260", ground: "#dde7dc", tree: "#75986c" },
  { sky: ["#e7dcc9", "#f8f3e8"], sun: "#f2e3bd", far: "#cfc0a5", mid: "#e6dbc6", near: "#f7f2e7", win: "#a68f68", roof: "#5c4c34", ground: "#e4e3cf", tree: "#8aa06a" },
];

/* สุ่มแบบกำหนดผลได้ (seed เดิม -> ผลเดิมทุกครั้ง) กัน layout กระพริบตอน re-render */
const rand = (seed) => {
  const x = Math.sin(seed * 12.9898) * 43758.5453;
  return x - Math.floor(x);
};

/* หน้าต่างบนตึก : จุดเล็ก ๆ เรียงเป็นตาราง */
const windows = (x, y, w, h, color, seed, cols = 4, gap = 9) => {
  const items = [];
  const size = 4;
  const rows = Math.max(1, Math.floor((h - 12) / gap));
  const colCount = Math.max(1, Math.min(cols, Math.floor((w - 8) / gap)));
  for (let r = 0; r < rows; r += 1) {
    for (let c = 0; c < colCount; c += 1) {
      const lit = rand(seed + r * 7 + c * 13) > 0.62;
      items.push(
        <rect
          key={`w-${x}-${y}-${r}-${c}`}
          x={x + 6 + c * gap}
          y={y + 8 + r * gap}
          width={size}
          height={size}
          rx="1"
          fill={lit ? "#fbe9b6" : color}
          opacity={lit ? 0.95 : 0.55}
        />,
      );
    }
  }
  return items;
};

const tree = (x, y, s, color, key) => (
  <g key={key}>
    <rect x={x - 1.5 * s} y={y - 3 * s} width={3 * s} height={6 * s} rx={1.5 * s} fill={color} />
    <ellipse cx={x} cy={y - 6 * s} rx={5 * s} ry={6 * s} fill={color} opacity="0.92" />
  </g>
);

/* ---------------------------------------------------------------- ฉากต่าง ๆ */
const scenes = {
  /* คอนโดสูง */
  tower: (c, seed) => {
    const towers = [
      { x: 40, w: 58, h: 176 },
      { x: 108, w: 74, h: 214 },
      { x: 192, w: 52, h: 150 },
      { x: 252, w: 68, h: 192 },
      { x: 328, w: 44, h: 132 },
    ];
    return (
      <g>
        {towers.map((tw, i) => {
          const top = 248 - tw.h - Math.round(rand(seed + i) * 18);
          const fill = i % 2 ? c.mid : c.near;
          return (
            <g key={`t-${i}`}>
              <rect x={tw.x} y={top} width={tw.w} height={248 - top} rx="3" fill={fill} />
              <rect x={tw.x} y={top} width={tw.w} height="6" rx="2" fill={c.roof} opacity="0.5" />
              {windows(tw.x, top, tw.w, 248 - top, c.win, seed + i * 31, 6)}
            </g>
          );
        })}
      </g>
    );
  },

  /* คอนโด low-rise / อาคารเตี้ย */
  lowrise: (c, seed) => (
    <g>
      <rect x="28" y="150" width="150" height="98" rx="4" fill={c.near} />
      {windows(28, 150, 150, 98, c.win, seed + 5, 14, 12)}
      <rect x="188" y="122" width="122" height="126" rx="4" fill={c.mid} />
      {windows(188, 122, 122, 126, c.win, seed + 9, 11, 12)}
      <rect x="318" y="168" width="66" height="80" rx="4" fill={c.near} />
      {windows(318, 168, 66, 80, c.win, seed + 12, 6, 12)}
      <rect x="0" y="240" width="400" height="12" fill={c.ground} />
    </g>
  ),

  /* บ้านเดี่ยว + สวน + สระ */
  house: (c) => (
    <g>
      <rect x="0" y="196" width="400" height="60" fill={c.ground} />
      <path d="M118 168 L200 116 L282 168 Z" fill={c.roof} />
      <rect x="136" y="166" width="128" height="76" rx="3" fill={c.near} />
      <rect x="152" y="182" width="26" height="26" rx="2" fill={c.win} opacity="0.75" />
      <rect x="188" y="182" width="26" height="26" rx="2" fill={c.win} opacity="0.75" />
      <rect x="224" y="182" width="26" height="26" rx="2" fill={c.win} opacity="0.75" />
      <rect x="184" y="214" width="32" height="28" rx="2" fill={c.roof} opacity="0.72" />
      <rect x="264" y="182" width="62" height="60" rx="3" fill={c.mid} />
      <path d="M258 184 L295 158 L332 184 Z" fill={c.roof} opacity="0.85" />
      <ellipse cx="196" cy="256" rx="120" ry="18" fill="#bfe0e6" opacity="0.9" />
      {tree(70, 242, 2.1, c.tree, "t1")}
      {tree(346, 240, 1.8, c.tree, "t2")}
      {tree(104, 234, 1.3, c.tree, "t3")}
    </g>
  ),

  /* ทาวน์โฮมเรียงแถว */
  town: (c) => (
    <g>
      <rect x="0" y="214" width="400" height="42" fill={c.ground} />
      {[24, 116, 208, 300].map((x, i) => (
        <g key={`th-${i}`}>
          <path d={`M${x - 4} 148 L${x + 40} 118 L${x + 84} 148 Z`} fill={c.roof} opacity={i % 2 ? 0.85 : 1} />
          <rect x={x} y="146" width="80" height="76" rx="3" fill={i % 2 ? c.mid : c.near} />
          <rect x={x + 12} y="160" width="24" height="20" rx="2" fill={c.win} opacity="0.7" />
          <rect x={x + 46} y="160" width="24" height="20" rx="2" fill={c.win} opacity="0.7" />
          <rect x={x + 30} y="194" width="22" height="28" rx="2" fill={c.roof} opacity="0.6" />
        </g>
      ))}
    </g>
  ),

  /* อาคารพาณิชย์ / โฮมออฟฟิศ */
  shop: (c, seed) => (
    <g>
      <rect x="0" y="228" width="400" height="30" fill={c.ground} />
      {[36, 126, 216, 306].map((x, i) => (
        <g key={`sh-${i}`}>
          <rect x={x} y={92 + (i % 2) * 10} width="76" height={140 - (i % 2) * 10} rx="2" fill={i % 2 ? c.near : c.mid} />
          <rect x={x} y={92 + (i % 2) * 10} width="76" height="8" fill={c.roof} opacity="0.55" />
          {windows(x, 100 + (i % 2) * 10, 76, 96, c.win, seed + i * 17, 7, 11)}
          <rect x={x + 10} y="196" width="56" height="34" rx="2" fill={c.roof} opacity="0.42" />
        </g>
      ))}
    </g>
  ),

  /* ที่ดินเปล่า */
  land: (c) => (
    <g>
      <rect x="0" y="176" width="400" height="90" fill={c.ground} />
      <path d="M0 200 Q200 176 400 206 L400 268 L0 268 Z" fill={c.tree} opacity="0.22" />
      <path d="M0 232 L400 214" stroke={c.mid} strokeWidth="16" opacity="0.75" />
      <path d="M0 232 L400 214" stroke="#ffffff" strokeWidth="1.5" strokeDasharray="12 10" opacity="0.7" />
      {[40, 96, 152, 208, 264, 320, 376].map((x, i) => (
        <rect key={`f-${i}`} x={x} y={168 - (i % 2) * 3} width="3" height="26" rx="1.5" fill={c.roof} opacity="0.5" />
      ))}
      <path d="M40 172 L376 164" stroke={c.roof} strokeWidth="2" opacity="0.4" />
      {tree(60, 200, 1.6, c.tree, "l1")}
      {tree(330, 196, 1.9, c.tree, "l2")}
      {tree(240, 190, 1.2, c.tree, "l3")}
    </g>
  ),

  /* เส้นขอบฟ้าเมือง (ใช้กับการ์ดทำเล) */
  skyline: (c, seed) => (
    <g>
      {Array.from({ length: 14 }).map((_, i) => {
        const w = 22 + Math.round(rand(seed + i) * 18);
        const h = 60 + Math.round(rand(seed + i * 3) * 150);
        const x = i * 29;
        return (
          <g key={`sk-${i}`}>
            <rect x={x} y={258 - h} width={w} height={h} rx="2" fill={i % 3 === 0 ? c.mid : c.far} opacity={0.75 + (i % 3) * 0.1} />
            {windows(x, 258 - h, w, h, c.win, seed + i * 11, 3, 10)}
          </g>
        );
      })}
      <rect x="0" y="252" width="400" height="16" fill={c.roof} opacity="0.28" />
    </g>
  ),

  /* ภาพประกอบบทความ : เอกสาร + กราฟ ในโทนเดียวกับการ์ดอื่น
     คุมคอนทราสต์ให้เข้มพอมองเห็นชัด แต่ยังไม่แย่งความสนใจจากหัวข้อบทความ */
  abstract: (c) => (
    <g>
      <circle cx="316" cy="74" r="62" fill={c.win} opacity="0.32" />
      <path d="M0 214 Q120 178 250 208 T400 196 L400 268 L0 268 Z" fill={c.tree} opacity="0.28" />

      {/* เอกสาร */}
      <rect x="52" y="96" width="132" height="150" rx="12" fill="#ffffff" opacity="0.94" />
      <rect x="52" y="96" width="132" height="150" rx="12" fill="none" stroke={c.roof} strokeWidth="2" opacity="0.35" />
      <rect x="72" y="120" width="66" height="9" rx="4.5" fill={c.roof} opacity="0.6" />
      <rect x="72" y="140" width="92" height="7" rx="3.5" fill={c.win} opacity="0.5" />
      <rect x="72" y="156" width="80" height="7" rx="3.5" fill={c.win} opacity="0.36" />
      <rect x="72" y="172" width="88" height="7" rx="3.5" fill={c.win} opacity="0.36" />
      <rect x="72" y="196" width="46" height="20" rx="6" fill={c.tree} opacity="0.7" />

      {/* กราฟแท่ง */}
      <rect x="206" y="128" width="118" height="118" rx="14" fill={c.roof} opacity="0.55" />
      <rect x="226" y="200" width="18" height="30" rx="4" fill="#ffffff" opacity="0.78" />
      <rect x="252" y="180" width="18" height="50" rx="4" fill="#ffffff" opacity="0.86" />
      <rect x="278" y="158" width="18" height="72" rx="4" fill="#ffffff" opacity="0.94" />
      <path d="M226 190 L256 168 L286 146" stroke="#ffffff" strokeWidth="3" fill="none" opacity="0.9" />
      <circle cx="286" cy="146" r="5" fill="#ffffff" />

      <rect x="336" y="158" width="44" height="88" rx="12" fill={c.far} />
      <rect x="348" y="178" width="20" height="6" rx="3" fill="#ffffff" opacity="0.7" />
      <rect x="348" y="192" width="20" height="6" rx="3" fill="#ffffff" opacity="0.5" />
    </g>
  ),
};

export default function HomeMedia({ kind = "tower", tone = 0, ratio = 0.68, src, alt = "", className = "", children }) {
  const c = TONES[tone % TONES.length];
  const seed = (tone + 1) * 17 + kind.length * 3;
  const scene = scenes[kind] || scenes.tower;
  const gid = `hm-sky-${kind}-${tone}`;

  return (
    <div className={`hm-media-box ${className}`} style={{ paddingTop: `${ratio * 100}%` }}>
      {src ? (
        <img className="hm-media" src={src} alt={alt} style={{ objectFit: "cover" }} />
      ) : (
        <svg className="hm-media" viewBox="0 0 400 268" preserveAspectRatio="xMidYMid slice" role="img" aria-label={alt}>
          <defs>
            <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={c.sky[0]} />
              <stop offset="100%" stopColor={c.sky[1]} />
            </linearGradient>
          </defs>
          <rect width="400" height="268" fill={`url(#${gid})`} />
          <circle cx="332" cy="52" r="30" fill={c.sun} opacity="0.55" />
          <circle cx="332" cy="52" r="16" fill={c.sun} opacity="0.75" />
          {scene(c, seed)}
          <rect width="400" height="268" fill="#142016" opacity="0.03" />
        </svg>
      )}
      {children}
    </div>
  );
}
